"use client";

import type { CSSProperties, MouseEvent } from "react";
import { PlayerPlay } from "@/components/ui/audiocn/player";

/**
 * TPlay — §03.4 "Play button" (03-Audio.dc.html), in AudioCN's form.
 *
 * "Seven sizes, one component." This is that component. There used to be two:
 * `play-btn.tsx` carried a 44px default and a third resting treatment the spec
 * does not have, and the design-system preview rendered it beside this one,
 * documenting the inconsistency as though it were intentional (TES-2310).
 *
 * The form is AudioCN's AudioPlayerPlay (components/ui/audiocn/player.tsx
 * names the source commit): a solid disc in every state, with a filled play
 * triangle and filled pause bars drawn at 0.4 of the diameter. The glyphs are
 * the Phosphor "fill" weight AudioCN's own players use (PlayIcon / PauseIcon,
 * MIT, Copyright (c) 2023 Phosphor Icons), inlined below so no icon package
 * is added. The triangle sits right of centre inside its own viewBox, which
 * carries §17.11's optical offset.
 *
 * AudioCN paints the disc in shadcn's primary with a hover fill. Here the two
 * rungs read our palette, and hover changes nothing (PRODUCT.md, hover
 * theatre):
 *
 *   ink    solid `--tls-fg-strong` — at rest; the next press plays
 *   accent solid `--tls-brand`     — playing right now
 *
 * §03.4's third rung, a 1.5px ring for "not loaded yet", is gone: AudioCN's
 * disc is never hollow, and every control resolves its audio on press anyway.
 * The ink fill spends none of §01.2's six accent roles, so it costs nothing
 * against the 8% area cap and leaves a panel's Download as its one accent
 * primary (the reasoning TES-2136 used for the sound page's disc).
 *
 * Buffering keeps whichever fill it started from: §03.4 requires the control
 * to report what you did on pointerdown, not what the audio did, so `loading`
 * reaches `aria-busy` only. AudioCN has no visible loading state either.
 *
 * No shadow and no border. Shadows belong to the four floating surfaces
 * (sheet, modal, mini player, consent), never to a control on a page panel.
 */

/**
 * §03.4's size ladder is 28 · 40 · 44 · 52 · 56 · 64 · 76, and `size` stays a
 * plain number rather than a union of those seven: the mini player's 28 is
 * admitted only because the whole bar is also tappable, which a type cannot
 * check. 44 is the default because it is the WCAG 2.5.5 floor.
 */
const LADDER_DEFAULT = 44;

/**
 * The ground the control is painted ON, which decides every colour below.
 *
 * A ground is NAMED here rather than handed in as four colours by the caller,
 * and that is the load-bearing decision. Every §03.4 fork this component has
 * absorbed started as a caller picking its own fill against its own surface,
 * and two of them picked a pair that failed WCAG 1.4.11 (TES-2322, TES-2344).
 * The ladder is the same three rungs on every ground; only which token each
 * rung reads changes. So the contrast reasoning lives in one table instead of
 * at each call site, and a fourth ground costs four tokens and a measurement.
 *
 * "canvas" is a normal page panel: ink on the page ground.
 *
 * "inverted" is a surface whose ground is `--tls-fg-strong` — §04.2's expanded
 * row plate is the only one so far — and there ink and canvas swap roles, so
 * the resting fill knocks out.
 *
 * The accent swaps too, and that is the part a caller cannot get right by
 * hand: the inverted band follows the theme, so on it the raw accent is
 * correct in dark (5.6:1 on the near-white band) and forbidden in light
 * (2.39:1 on near-black, PRODUCT.md), and the lift is the exact reverse.
 * `--tls-brand-on-inverted` resolves that per theme in one place.
 *
 * "hero" is the homepage hero panel, which is neither of those. It is a
 * theme-authored plate (`--tls-surface-elevated` in dark, `--tls-surface` in
 * light) with a photograph behind it and a scrim mixed FROM its own ground —
 * so it is a canvas-family surface, and its ink IS `canvas`'s ink in both
 * themes. Its accent is not. In dark, raw `--tls-brand` measures 2.72:1
 * against the hero plate, under §1.4.11's 3:1 for the playing disc's own edge,
 * so the hero authors the lift (`--tls-brand-ink`, 6.28:1) and pairs it with
 * canvas ink rather than `--tls-brand-fg`: 7.14:1 against 2.44:1, which is the
 * WCAG 1.4.3 failure axe caught on the hero's primary CTA in TES-1926. In
 * light the pair collapses back onto `canvas`'s. One ground member carries
 * both halves of that; `canvas` carries neither. The values themselves live in
 * the `--tls-hero-*` block in globals.css, which is where they stay.
 */
export type TPlayGround = "canvas" | "inverted" | "hero";

/**
 * Which token each rung reads, per ground — one row per ground, so a new one is
 * a measurement rather than another branch.
 *
 *   ink       the resting fill
 *   under     the glyph knocked out of that ink fill: the ground behind it
 *   accent    the playing fill
 *   accentFg  the glyph on the playing fill
 */
const GROUND_TOKENS: Record<
  TPlayGround,
  { ink: string; under: string; accent: string; accentFg: string }
> = {
  canvas: {
    ink: "var(--tls-fg-strong)",
    under: "var(--tls-canvas)",
    accent: "var(--tls-brand)",
    accentFg: "var(--tls-brand-fg)",
  },
  inverted: {
    ink: "var(--tls-canvas)",
    under: "var(--tls-fg-strong)",
    accent: "var(--tls-brand-on-inverted)",
    accentFg: "var(--tls-brand-on-inverted-fg)",
  },
  hero: {
    ink: "var(--tls-hero-fg)",
    under: "var(--tls-hero-bg)",
    accent: "var(--tls-hero-accent)",
    accentFg: "var(--tls-hero-accent-fg)",
  },
};

/** The two rungs, resolved against the ground the disc is painted on. */
function ladder(ground: TPlayGround, playing: boolean) {
  const { ink, under, accent, accentFg } = GROUND_TOKENS[ground];
  return playing ? { fill: accent, glyph: accentFg } : { fill: ink, glyph: under };
}

/** Phosphor play-fill and pause-fill, the glyphs AudioCN's players render. */
const PLAY_PATH =
  "M240,128a15.74,15.74,0,0,1-7.6,13.51L88.32,229.65a16,16,0,0,1-16.2.3A15.86,15.86,0,0,1,64,216.13V39.87a15.86,15.86,0,0,1,8.12-13.82,16,16,0,0,1,16.2.3L232.4,114.49A15.74,15.74,0,0,1,240,128Z";
const PAUSE_PATH =
  "M216,48V208a16,16,0,0,1-16,16H160a16,16,0,0,1-16-16V48a16,16,0,0,1,16-16h40A16,16,0,0,1,216,48ZM96,32H56A16,16,0,0,0,40,48V208a16,16,0,0,0,16,16H96a16,16,0,0,0,16-16V48A16,16,0,0,0,96,32Z";

/**
 * §11.4's target floor, and the point where `size` stops being the whole
 * story. Below it the disc paints at `size` and `.tls-play-target` supplies
 * the 44px target as an out-of-flow pseudo-element — §02.4's "hit area padded
 * to 44" applied to a circle (TES-2312). At or above it the disc IS the
 * target, so the pad would be a 44px box inside a larger one: dead weight,
 * and a z-index:1 layer over the paint for no reason.
 *
 * The floor previously sat on the painted element as min-width/min-height,
 * which is exactly the collapse PRODUCT.md rules against under "the 44px
 * target is a padded wrapper, not a taller button": 28 and 40 both painted 44.
 *
 * A `size="css"` control cannot be compared against this at render time, so it
 * declares the class from its own rule if it ever drops below 44. The sound
 * row's rungs are 44 and 52, both at or above the floor.
 */
const TOUCH_FLOOR = 44;

interface TPlayProps {
  playing: boolean;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  /**
   * One of §03.4's seven sizes. Defaults to 44, the WCAG 2.5.5 floor.
   *
   * `"css"` hands the diameter to the caller's stylesheet via
   * `--tls-play-size`, which the disc and the glyph both scale from. §04.1's
   * density axis is a media query, so the sound row's rung (44 at d1, 52 at
   * d0) is not knowable at render time on the server — and its SSR placeholder
   * has to paint at the same diameter as the live row or hydration jumps.
   */
  size?: number | "css";
  /** Subject of the control (normally the track title) for the stable name. */
  title?: string;
  /** True while the track is resolving or buffering. Carried by aria-busy only. */
  loading?: boolean;
  /** The ground this control is painted on. See {@link TPlayGround}. */
  ground?: TPlayGround;
  /**
   * Render an inert `<span aria-hidden>` instead of a button: same box, same
   * paint, no name and no tab stop. For an SSR placeholder row that upgrades to
   * a live control on intersection — nobody should be offered a transport that
   * is not wired to audio yet.
   */
  decorative?: boolean;
  className?: string;
}

export function TPlay({
  onClick,
  playing,
  size = LADDER_DEFAULT,
  title,
  loading,
  ground = "canvas",
  decorative = false,
  className,
}: TPlayProps) {
  const cssSized = size === "css";
  // Below the floor the 44px target comes from the pseudo-element instead of
  // from the painted box; at or above it the disc already is the target. A
  // CSS-sized control cannot be measured here, so it opts in through the same
  // class from its own rule — and at 44 and above the pad is inert anyway.
  const padded = typeof size === "number" && size < TOUCH_FLOOR;

  const paint = ladder(ground, playing);
  const diameter = "var(--tls-play-size)";

  const style: CSSProperties = {
    // A number writes the property; "css" leaves it to the caller's rule.
    ...(cssSized ? null : { ["--tls-play-size" as string]: `${size}px` }),
    width: diameter,
    height: diameter,
    aspectRatio: "1 / 1",
    padding: 0,
    borderRadius: "50%",
    background: paint.fill,
    color: paint.glyph,
    border: "none",
    boxShadow: "none",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    // An inline `position` would outrank the caller's own rule, and the sound
    // row repositions this control absolutely inside the expanded plate.
    ...(cssSized ? null : { position: "relative" as const }),
  };

  const glyph = (
    <svg
      aria-hidden="true"
      style={{ width: `calc(${diameter} * 0.4)`, height: `calc(${diameter} * 0.4)` }}
      viewBox="0 0 256 256"
      fill="currentColor"
    >
      <path d={playing ? PAUSE_PATH : PLAY_PATH} />
    </svg>
  );

  if (decorative) {
    return (
      // eslint-disable-next-line react-doctor/no-inline-exhaustive-style
      <span aria-hidden="true" className={className} style={style}>
        {glyph}
      </span>
    );
  }

  return (
    // PlayerPlay is the AudioCN-derived button shell: it owns the element, the
    // state attributes and the a11y contract (stable name, aria-pressed,
    // aria-busy, never disabled, TES-1581). The paint is this component's.
    <PlayerPlay
      onClick={onClick}
      playing={playing}
      loading={loading}
      title={title}
      // data-icon-btn opts out of the global @media (pointer: coarse) button
      // min-height rule, which only stretches height and would render an oval
      // hit-target on a control whose width is its `size`. WCAG 2.5.5 is paid
      // by `.tls-play-target` instead — a square 44px pseudo-element centred
      // on the disc — so the paint keeps the diameter the caller asked for.
      data-icon-btn
      className={[
        // §03.4 measures press as scale(0.97) + opacity 0.85. Both, not one:
        // the scale alone was shipped and reads as a nudge rather than a press.
        "motion-safe:active:scale-[0.97] motion-safe:active:opacity-[0.85]",
        "tlsr-focus",
        padded ? "tls-play-target" : "",
        className ?? "",
      ]
        .filter(Boolean)
        .join(" ")}
      // eslint-disable-next-line react-doctor/no-inline-exhaustive-style
      style={{
        ...style,
        cursor: "pointer",
        // Both halves of the press ease together. Naming only `scale` here
        // left the opacity step snapping instantly against an 80ms scale,
        // which reads as a flicker rather than one press.
        transition:
          "scale var(--tls-duration-fast) var(--tls-ease), opacity var(--tls-duration-fast) var(--tls-ease)",
      }}
    >
      {glyph}
    </PlayerPlay>
  );
}
